import React, { useState, useContext, useCallback, useEffect, useRef, useMemo } from 'react';
import {
      CModal,
      CModalHeader,
      CModalBody,
      CModalTitle,
      CButton,
      CRow,
      CCol,
      CCard,
      CCardImage,
      CCardBody,
      CBadge,
} from '@coreui/react';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';
import Logger from '../utils/Logger';
import { AuthUserContext } from '../views/session';
import { ActiveLinkContext } from './ActiveLinkContext';
import { useCart } from '../context/CartContext';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import MarketplaceService from '../services/MarketplaceService';
import config from '../utils/EnvironmentSwitcher';
import { downloadOwnedNftAsset } from '../utils/downloadAsset';

const ArtworkDetailModal = ({ show, onHide, artwork: propArtwork, isMarketplace = false }) => {
      const auth = getAuth();
      const { authUser } = useContext(AuthUserContext);
      const currentUser = auth.currentUser;
      const { setActiveLink } = useContext(ActiveLinkContext);
      const { addToCart } = useCart();
      const navigate = useNavigate();
      const location = useLocation();
      const { listingId } = useParams();
      
      const handleClose = useCallback(() => {
            navigate(location.pathname.split('/listing/')[0], { 
                  replace: true 
            });
            onHide();
      }, [navigate, onHide, location]);

      // Initialize MarketplaceService
      const marketplaceService = new MarketplaceService();

      // Memoize location state art to prevent unnecessary re-renders
      const locationStateArt = useMemo(() => location.state?.art, [location.state?.art]);
      
      // Initialize artwork from propArtwork or location state
      const [artwork, setArtwork] = useState(() => propArtwork || locationStateArt);
      const artworkRef = useRef(artwork); // Track artwork in ref to avoid dependency issues
      const lastPropArtworkRef = useRef(propArtwork); // Track last propArtwork to prevent unnecessary updates
      const lastLocationStateRef = useRef(locationStateArt); // Track last location state

      // Update ref when artwork changes
      useEffect(() => {
            artworkRef.current = artwork;
      }, [artwork]);

      // Single useEffect to handle all artwork initialization and fetching
      useEffect(() => {
            // Check if we have artwork from any source (use ref to avoid dependency)
            const hasArtwork = propArtwork || locationStateArt || artworkRef.current;
            
            // Priority 1: If propArtwork is provided and different from last, use it immediately
            if (propArtwork && propArtwork !== lastPropArtworkRef.current) {
                  Logger.debug('ArtworkDetailModal: Using propArtwork directly');
                  setArtwork(propArtwork);
                  lastPropArtworkRef.current = propArtwork;
                  return; // Exit early, don't fetch
            }
            
            // Priority 2: If we have location state and different from last, use it
            if (locationStateArt && locationStateArt !== lastLocationStateRef.current) {
                  Logger.debug('ArtworkDetailModal: Using location state');
                  setArtwork(locationStateArt);
                  lastLocationStateRef.current = locationStateArt;
                  return; // Exit early, don't fetch
            }
            
            // Priority 3: If we already have artwork from any source, preserve it and don't fetch
            if (hasArtwork) {
                  Logger.debug('ArtworkDetailModal: Preserving existing artwork state');
                  return; // Exit early, don't fetch
            }
            
            // Priority 4: Only fetch if we have listingId but no artwork data available
            if (listingId) {
                  Logger.debug('ArtworkDetailModal: Fetching artwork', { listingId, isMarketplace });
                  const fetchArtwork = async () => {
                        try {
                              let data = null;
                              
                              // In MyStuff (isMarketplace=false), fetch by NFT ID only
                              // In Marketplace (isMarketplace=true), fetch by listing ID first, then NFT ID as fallback
                              if (isMarketplace) {
                                    // Marketplace: Try listing ID first
                                    Logger.debug('ArtworkDetailModal: Marketplace mode - fetching as listing ID first');
                                    data = await marketplaceService.getListingById(listingId);
                                    
                                    // If that fails or returns null, try fetching as NFT ID
                                    if (!data) {
                                          Logger.debug('Listing not found, trying to fetch as NFT ID');
                                          try {
                                                data = await marketplaceService.getNFTById(listingId);
                                                if (data) {
                                                      Logger.debug('Successfully fetched as NFT');
                                                }
                                          } catch (nftError) {
                                                Logger.debug('Also failed to fetch as NFT', nftError);
                                          }
                                    }
                              } else {
                                    // MyStuff: Fetch by NFT ID only (no listing ID attempt)
                                    Logger.debug('ArtworkDetailModal: MyStuff mode - fetching as NFT ID only');
                                    try {
                                          data = await marketplaceService.getNFTById(listingId);
                                          if (data) {
                                                Logger.debug('Successfully fetched as NFT');
                                          }
                                    } catch (nftError) {
                                          Logger.debug('Failed to fetch as NFT', nftError);
                                    }
                              }
                              
                              if (data) {
                                    setArtwork(data);
                              } else {
                                    // If we couldn't fetch and we don't have existing artwork, show error and close
                                    Logger.debug('Could not fetch artwork and no existing artwork available');
                                    toast.error('Artwork not found');
                                    handleClose();
                              }
                        } catch (error) {
                              Logger.error('Error fetching artwork:', error);
                              toast.error('Failed to load artwork details');
                              handleClose();
                        }
                  };
                  fetchArtwork();
            }
      }, [listingId, propArtwork, handleClose, isMarketplace, locationStateArt]);

      //Logger.debug('Artwork Detail Modal artwork:', JSON.stringify(artwork));
      
      // Use artwork from state, prop, or location state (in that priority order)
      // Check all sources directly to avoid memoization issues
      const displayArtwork = artwork || propArtwork || locationStateArt;
      const hasArtworkData = !!displayArtwork;
      
      // Don't render if we don't have artwork AND we're not showing the modal
      if (!hasArtworkData && !show && !listingId) {
            return null;
      }
      
      // If we're showing the modal but don't have artwork yet (and we're actually fetching), show loading state
      // Only show loading if we don't have artwork AND we have a listingId to fetch
      if (!hasArtworkData && (show || listingId) && listingId) {
            return (
                  <CModal 
                        visible={show || !!listingId} 
                        onClose={handleClose} 
                        size="lg"
                  >
                        <CModalHeader onClose={handleClose}>
                              <CModalTitle>Loading...</CModalTitle>
                        </CModalHeader>
                        <CModalBody>
                              <div className="text-center py-4">
                                    <p>Loading artwork details...</p>
                              </div>
                        </CModalBody>
                  </CModal>
            );
      }
      
      // If we still don't have artwork at this point, don't render
      if (!hasArtworkData) {
            return null;
      }
      //console.log(stripeConnectInstance)
      //console.log(authUser)
      // Owner may be stored as owner, creator, or sellerID depending on API context
      const ownerId = displayArtwork.owner || displayArtwork.creator || displayArtwork.sellerID;
      const isOwner = !!currentUser?.uid && currentUser.uid === ownerId;
      const isDigitalMedia = String(displayArtwork.itemMedia || '').toLowerCase() !== 'physical';
      const assetCid = displayArtwork.asset
            || (displayArtwork.assetUrl && displayArtwork.assetUrl.split('/ipfs/').pop()?.split('?')[0]);
      //console.log('Listing ID from artwork:', JSON.stringify(displayArtwork));
      const renderActionButton = () => {
            if (isMarketplace) {
                  return (
                        <div className="d-grid gap-2">
                              <CButton
                                    color="primary"
                                    size="lg"
                                    onClick={() => {
                                          addToCart(displayArtwork);
                                          toast.success('Added to cart');
                                          onHide();
                                    }}
                                    className="d-grid"
                              >
                                    Add to Cart
                              </CButton>
                              <CButton
                                    color="secondary"
                                    size="lg"
                                    onClick={() => {
                                          navigate('/cart');
                                          onHide();
                                    }}
                                    className="d-grid"
                              >
                                    View Cart
                              </CButton>
                        </div>
                  );
            }

            // MyStuff: owner downloads via authenticated API proxy (not public IPFS gateway)
            if (isOwner && isDigitalMedia && (displayArtwork.nftId || assetCid)) {
                  return (
                        <CButton
                              color="success"
                              size="lg"
                              onClick={async () => {
                                    try {
                                          if (!currentUser) {
                                                toast.error('Please sign in to download');
                                                return;
                                          }
                                          const idToken = await currentUser.getIdToken();
                                          await downloadOwnedNftAsset({
                                                apiUrl: config.apiUrl,
                                                idToken,
                                                nftId: displayArtwork.nftId,
                                                ipfsUrl: assetCid
                                                      ? `https://ipfs.mieros.cloud/ipfs/${assetCid}`
                                                      : displayArtwork.assetUrl || displayArtwork.itemAssetUrl,
                                                assetName: displayArtwork.assetName || displayArtwork.itemAssetName,
                                                assetType: displayArtwork.assetType || displayArtwork.itemAssetType,
                                          });
                                    } catch (err) {
                                          console.error(err);
                                          toast.error(err.message || 'Download failed');
                                    }
                              }}
                              className="d-grid"
                        >
                              <i className="fas fa-download me-2"></i>
                              Download Digital Asset
                        </CButton>
                  );
            }

            if (String(displayArtwork.itemMedia || '').toLowerCase() === 'physical') {
                  return (
                        <div className="mb-2 text-center">
                              Physical item - No digital asset available
                        </div>
                  );
            }

            return null;
      };

      const formatDate = (dateString) => {
            return new Date(dateString).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
            });
      };

      const imageUrl = displayArtwork.metadata?.image
            ? `https://ipfs.mieros.cloud/ipfs/${displayArtwork.metadata.image}`
            : displayArtwork.imageUrl || displayArtwork.ipfsHash;

      return (
            <CModal 
                  visible={show || !!listingId} 
                  onClose={handleClose} 
                  size="lg"
            >
                  <CModalHeader onClose={handleClose}>
                        <CModalTitle>{displayArtwork.itemDetail || displayArtwork.metadata?.title}</CModalTitle>
                  </CModalHeader>
                  <CModalBody>
                        <CRow>
                              <CCol md={6}>
                                    <CCard>
                                          <CCardImage
                                                orientation="top"
                                                src={imageUrl}
                                                style={{ maxHeight: '400px', objectFit: 'contain' }}
                                          />
                                    </CCard>
                              </CCol>
                              <CCol md={6}>
                                    <div className="mb-3">
                                          <h5>Price</h5>
                                          <p className="text-primary fw-bold">{displayArtwork.price} USD</p>
                                    </div>

                                    <div className="mb-3">
                                          <h5>Description</h5>
                                          <p>{displayArtwork.description || displayArtwork.metadata.description || 'Parsing Error'}</p>
                                    </div>

                                    <div className="mb-3">
                                          <h5>Details</h5>
                                          <div className="d-flex flex-column gap-2">
                                                <div>
                                                      <strong>NFT ID:</strong> <span className="mb-2">{displayArtwork.nftId}</span>
                                                </div>
                                                <div>
                                                      <strong>Listing ID:</strong> <span className="mb-2">{displayArtwork.listingID || 'N/A'}</span>
                                                </div>
                                                <div>
                                                      <strong>Item Type:</strong> <span className="mb-2">{displayArtwork.itemType}</span>
                                                </div>
                                                <div>
                                                      <strong>Category:</strong> <span className="mb-2">{displayArtwork.itemCategory}</span>
                                                </div>
                                                <div>
                                                      <strong>Media:</strong> <span className="mb-2">{displayArtwork.itemMedia}</span>
                                                </div>
                                                <div>
                                                      <strong>Royalty:</strong> <span className="mb-2">{displayArtwork.royalty} MiEROS</span>
                                                </div>
                                                <div>
                                                      <strong>Status:</strong>
                                                      <CBadge color={displayArtwork.status === 'LISTED' ? 'success' : 'warning'} className="ms-2">
                                                            {displayArtwork.status}
                                                      </CBadge>
                                                </div>
                                          </div>
                                    </div>

                                    <div className="mb-3">
                                          <h5>Categories & Subjects</h5>
                                          <div className="d-flex flex-wrap gap-2">
                                                {displayArtwork.categories?.map((category, index) => (
                                                      <CBadge key={`cat-${index}`} color="info">{category}</CBadge>
                                                ))}
                                                {displayArtwork.subjects?.map((subject, index) => (
                                                      <CBadge key={`sub-${index}`} color="secondary">{subject}</CBadge>
                                                ))}
                                          </div>
                                    </div>

                                    <div className="mb-3">
                                          <h5>Timestamps</h5>
                                          <div>
                                                <strong>Created:</strong> <span className="mb-2">{formatDate(displayArtwork.createdAt)}</span>
                                          </div>
                                       
                                    </div>

                                    <div className="mb-3">
                                          <h5>Transaction Details</h5>
                                          <div>
                                                <strong>Last Updated TxID:</strong>
                                                <span className="mb-2 d-block text-truncate">{displayArtwork.lastUpdatedTxID}</span>
                                          </div>
                                          <div>
                                                <strong>Seller ID:</strong>
                                                <span className="mb-2 d-block text-truncate">{displayArtwork.sellerID}</span>
                                          </div>
                                    </div>

                                    <div className="d-grid gap-2">
                                          {renderActionButton()}
                                    </div>
                              </CCol>
                        </CRow>
                  </CModalBody>
            </CModal>
      );
};

export default ArtworkDetailModal; 

